import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  ActiveSuggestedAction,
  PausedSuggestedAction,
  SuggestedActionsResponse,
} from 'router/subrouters/DataRoom/models/Dashboard';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { SUGGESTED_ACTIONS_QUERY_KEY } from '../../../hooks/useSuggestedActionsQuery';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { useTranslation } from 'react-i18next';

const useGetPausedSuggestedAction = (
  pausedSuggestion: PausedSuggestedAction,
) => {
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { popSuccess } = useContext(PopNotificationsContext);

  const { type, count, names, expiredAt } = pausedSuggestion || {};

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const restoreActiveSuggestion = useCallback(
    (activeSuggestion: ActiveSuggestedAction) => {
      queryClient.setQueriesData<SuggestedActionsResponse>(
        { queryKey: [SUGGESTED_ACTIONS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            active: [...oldData.active, activeSuggestion],
            paused: oldData.paused.filter((item) => item.type !== type),
          };
        },
      );
    },
    [queryClient, type],
  );

  const [onRestore, isRestoring] = useCallbackAsync(async () => {
    await dataRoomApi.room.restoreMutedSuggestedAction(_id, type);

    restoreActiveSuggestion({
      type,
      count,
      names,
      expiredAt,
    });
    popSuccess(t('suggestionRestored'));
  });

  return { isRestoring, onRestore };
};

export default useGetPausedSuggestedAction;
